import React, { useState } from "react";
import { C } from "../../../components/cameras/CamerasStyles";
import { ShieldIcon, SnoozeForm, WATCH_TONE } from "../../../components/cameras/SecurityPill";
import {
  panelFoot,
  pillView,
  pillWhy,
  presenceRows,
  pressEndSnooze,
  pressImHere,
  pressSnooze,
  shopClock,
} from "../../../features/security/nightWatch";
import { phoneUi } from "../../ui/phoneUi";

// The phone Cameras screen's night-watch line (a text arrives at 2 AM, you open JobQ, one tap): the same
// words as the desktop pill and the same two buttons, through the shared actions with the phone's dialogs.
// Layout only: rules and calls are features/security/nightWatch.js.

export default function NightWatchBar({ status, error, cameras, onChanged }) {
  const [open, setOpen] = useState(false);
  const [snoozing, setSnoozing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  // A refused / failed read with nothing loaded yet says so (never "Night watch…" forever).
  const v = !status && error ? { state: "error", tone: "warn", main: "Night watch", side: "not loaded" } : pillView(status);
  const tone = WATCH_TONE[v.tone] || C.faint;
  const snooze = status?.snooze;
  const manualHere = (status?.armed?.present || []).some((p) => p.source === "manual");

  const act = async (fn, done) => {
    setBusy(true);
    setNote("");
    try {
      const r = await fn();
      if (r && r.ok) {
        setNote(done(r.body || {}));
        setSnoozing(false);
        if (onChanged) onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "8px 12px", borderBottom: "1px solid #22272d", display: "grid", gap: 8 }} data-testid="phone-night-watch">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: 0, padding: 0, color: "inherit", textAlign: "left", fontSize: 13 }}>
        {v.tone === "bad" ? <span className="jqc-dot" style={{ background: tone }} /> : <ShieldIcon color={tone} />}
        <span style={{ color: v.tone === "watch" ? C.text : tone, fontWeight: 600 }}>{v.main}</span>
        {v.side && <span style={{ color: C.muted }}>· {v.side}</span>}
      </button>
      {error && <span style={{ color: C.bad, fontSize: 12 }}>{error}</span>}
      {open && (
        <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
          <span style={{ color: C.textSoft }}>{pillWhy(status)}</span>
          {presenceRows(status).map((r) => (
            <div key={r.key} style={{ display: "flex", gap: 8, borderTop: `1px solid ${C.border}`, padding: "5px 0" }}>
              <span style={{ flex: 1 }}>{r.who} <span style={{ fontSize: 11, color: C.faint, textTransform: "uppercase" }}>{r.source}</span></span>
              <span style={{ color: r.long ? C.warn : C.muted, fontSize: 12, textAlign: "right" }}>{r.since}{r.since && r.until ? <br /> : null}{r.until}</span>
            </div>
          ))}
          <div style={{ fontSize: 12, color: C.muted, display: "grid", gap: 2, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
            {panelFoot(status).map((l) => <span key={l}>{l}</span>)}
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <button type="button" className="jqc-btn" disabled={busy}
                onClick={() => act(() => pressImHere({ ui: phoneUi, event: "arrive" }), (b) => `${b.person || "You"} count${b.person ? "s" : ""} as here now.`)}>
          I'm here
        </button>
        {manualHere && (
          <button type="button" className="jqc-btn" disabled={busy}
                  onClick={() => act(() => pressImHere({ ui: phoneUi, event: "leave" }), (b) => `${b.person || "You"} left.`)}>
            I'm leaving
          </button>
        )}
        {snooze ? (
          <>
            <button type="button" className="jqc-btn" disabled={busy}
                    onClick={() => act(() => pressEndSnooze({ ui: phoneUi, snoozeId: snooze.snooze_id }), () => "Armed again.")}>
              Arm now
            </button>
            <button type="button" className="jqc-btn" disabled={busy}
                    onClick={() => act(() => pressSnooze({ ui: phoneUi, scope: "whole", minutes: 30 }),
                      (b) => `Snoozed until ${shopClock(b.snooze?.until)}.`)}>
              +30 min
            </button>
          </>
        ) : (
          <button type="button" className="jqc-btn" disabled={busy} aria-expanded={snoozing} onClick={() => setSnoozing((s) => !s)}>Snooze</button>
        )}
      </div>
      {snoozing && (
        <SnoozeForm cameras={(cameras || []).filter((c) => !(status?.watcher?.cameras_ignored || []).includes(c.key))} busy={busy}
                    onCancel={() => setSnoozing(false)}
                    onSubmit={(f) => act(() => pressSnooze({ ui: phoneUi, ...f }),
                      (b) => `Snoozed ${f.scope === "whole" ? "the whole shop" : "that camera"} until ${shopClock(b.snooze?.until)}.`)} />
      )}
      {note && <span role="status" style={{ fontSize: 13, color: C.live }}>{note}</span>}
    </div>
  );
}
